import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export interface MarkdownMessageProps {
    /** The message content to render (may contain markdown) */
    content: string;
}

/**
 * Component for rendering message content with markdown support
 * Handles both plain text and markdown-formatted messages
 *
 * Features:
 * - GitHub Flavored Markdown (tables, strikethrough, task lists)
 * - Code blocks with syntax highlighting
 * - Links, lists, and formatting
 */
export function MarkdownMessage({content}: MarkdownMessageProps) {
    // If content is empty, return a placeholder
    if (!content || content.trim() === '') {
        return <span className="empty-message">&nbsp;</span>;
    }

    // Convert escaped newlines to actual newlines
    // This handles cases where the backend sends literal \n characters
    const processedContent = content.replace(/\\n/g, '\n');

    return (
        <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
                // Style code blocks
                code: ({node, inline, className, children, ...props}) => {
                    return inline ? (
                        <code className="inline-code" {...props}>
                            {children}
                        </code>
                    ) : (
                        <pre className="code-block">
                            <code className={className} {...props}>
                                {children}
                            </code>
                        </pre>
                    );
                },
                // Style links
                a: ({node, children, ...props}) => (
                    <a {...props} target="_blank" rel="noopener noreferrer">
                        {children}
                    </a>
                ),
                // Style tables
                table: ({node, children, ...props}) => (
                    <div className="markdown-table-wrapper">
                        <table className="markdown-table" {...props}>
                            {children}
                        </table>
                    </div>
                ),
                // Ensure paragraphs render properly
                p: ({node, children, ...props}) => <p {...props}>{children}</p>,
            }}
        >
            {processedContent}
        </ReactMarkdown>
    );
}
